import './kp-comments.less';
import type {Component} from 'typings/portaro.fe.types';
import type {Auth, Comment, RichDocument} from 'typings/portaro.be.types';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type CommentsDataService from '../../data-services/comments.data-service';
import type LoginService from '../../../login/login.service';
import type FinishedResponseInteractionService from '../../services/finished-response-interaction.service';
import type {IOnInit} from 'angular';

interface KpCommentsComponentBindings {
    document: RichDocument;
    anonymousComments: boolean;
}

/**
 * @ngdoc component
 * @name kpComments
 * @module portaro.components
 *
 * @param {RichDocument} document unique document id
 * @param {boolean} anonymousComments allow to add anonymous comments
 *
 * @description
 * Component load comments from server to each record
 */
export default class KpCommentsComponent implements Component<KpCommentsComponentBindings> {
    public static componentName = 'kpComments';

    public templateUrl = require('./kp-comments.tpl.ftl');
    public bindings = {
        document: '<',
        anonymousComments: '<'
    } as const;
    public controller = KpCommentsComponentController;
}

class KpCommentsComponentController implements KpCommentsComponentBindings, IOnInit {
    public document: RichDocument;
    public anonymousComments: boolean;

    public comments: Comment[];

    /*@ngInject*/
    protected constructor(private commentsDataService: CommentsDataService,
                          private loginService: LoginService,
                          private currentAuth: Auth,
                          private finishedResponseInteractionService : FinishedResponseInteractionService) {
    }

    public $onInit(): void {
        this.loadComments();
    }

    public addAnonymousComment() {
        return this.addComment({record: this.document.id});
    }

    @ngAsync()
    public async addNonAnonymousComment() {
        try {
            await this.loginService.login();
            await this.addComment({record: this.document.id, creator: this.currentAuth.activeUser.id});
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        } finally {
            this.loginService.reloadIfChangedButNotReloaded();
        }
    }

    @ngAsync()
    private async loadComments() {
        try {
            this.comments = await this.commentsDataService.loadComments(this.document.id);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
        }
    }

    @ngAsync()
    public async deleteComment(comment): Promise<void> {
        try {
            const response = await this.commentsDataService.deleteComment(comment);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(response);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        } finally {
            await this.loadComments();
        }
    }

    @ngAsync()
    private async addComment(commentCreation): Promise<void> {
        try {
            const response = await this.commentsDataService.addComment(commentCreation);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(response);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        } finally {
            await this.loadComments();
        }
    }
}
